import { isTabletDown, isTabletUp } from '../Utility/Breakpoint'
import {getClosestElement, getElement, getElementChildren, getElements} from '../Utility/Dom'

class NavMainDesktop {
  private readonly indicator: HTMLElement | null
  private readonly layerWrap: HTMLElement | null
  private readonly navButtons: NodeListOf<HTMLElement>
  private readonly navItems: NodeListOf<HTMLElement>
  private readonly tabButtons: NodeListOf<HTMLElement>

  constructor() {
    this.tabButtons = getElements('nav-tab-button')
    this.layerWrap = getElement('nav-layer-wrap')
    this.indicator = getElement('nav-main-indicator')
    this.navItems = getElements('nav-main-item')
    this.navButtons = getElements('nav-layer-button')
  }

  public init(): void {
    this.addNavButtonEvents()
    this.addTabEvents()
    this.addIndicatorEvents()
  }

  private activateTab(activeButton: HTMLElement): void {
    const tabsContainer = getClosestElement(activeButton, 'nav-tabs-container')
    if (!tabsContainer) return

    const containerButtons = getElementChildren(tabsContainer, 'nav-tab-button')

    containerButtons.forEach((btn: Element): void => {
      btn.setAttribute('aria-expanded', 'false')
    })

    activeButton.setAttribute('aria-expanded', 'true')
  }

  private addIndicatorEvents(): void {
    if (!this.layerWrap || !this.indicator || this.navItems.length === 0) return

    const activeItem = this.getActiveNavItem()
    if (activeItem) {
      this.updateIndicator(activeItem)
    }

    this.navItems.forEach((item: HTMLElement): void => {
      item.addEventListener('mouseenter', (): void => {
        if (isTabletUp()) {
          this.updateIndicator(item)
        }
      })
    })

    this.layerWrap.addEventListener('mouseleave', (): void => {
      if (isTabletUp()) {
        const activeItem = this.getActiveNavItem()
        if (activeItem) {
          this.updateIndicator(activeItem)
        }
      }
    })

    window.addEventListener('resize', (): void => {
      if (isTabletUp()) {
        const activeItem = this.getActiveNavItem()
        if (activeItem) {
          this.updateIndicator(activeItem)
        }
      }
    })
  }

  private addNavButtonEvents(): void {
    if (this.navButtons.length === 0) return

    this.navButtons.forEach((button: HTMLElement): void => {
      button.addEventListener('click', (event: Event): void => {
        if (isTabletDown()) return

        event.preventDefault()
        const isExpanded = button.getAttribute('aria-expanded') === 'true'

        this.closeAllDropdowns()

        if (!isExpanded) {
          button.setAttribute('aria-expanded', 'true')
        }
      })
    })

    document.addEventListener('keydown', (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && isTabletUp()) {
        this.closeAllDropdowns()
      }
    })

    document.addEventListener('click', (event: MouseEvent): void => {
      if (isTabletDown()) return

      const target = event.target as HTMLElement
      const isInsideNav = getClosestElement(target, 'nav-main-item')

      if (!isInsideNav) {
        this.closeAllDropdowns()
      }
    })
  }

  private addTabEvents(): void {
    if (this.tabButtons.length === 0) return

    this.tabButtons.forEach((button: HTMLElement): void => {
      button.addEventListener('click', (): void => {
        if (isTabletDown()) return
        this.activateTab(button)
      })
    })
  }

  private closeAllDropdowns(): void {
    this.navButtons.forEach((button: HTMLElement): void => {
      button.setAttribute('aria-expanded', 'false')
    })
  }

  private getActiveNavItem(): HTMLElement | null {
    for (const item of this.navItems) {
      const activeElement = item.querySelector('[data-js="nav-layer-button"].is-active, [data-js="nav-main-link"].is-active')

      if (activeElement) {
        return item
      }
    }

    return null
  }

  private updateIndicator(item: HTMLElement): void {
    if (!this.layerWrap || !this.indicator) return

    const button = item.querySelector('[data-js="nav-layer-button"], [data-js="nav-main-link"]') as HTMLElement
    if (!button) return

    const buttonRect = button.getBoundingClientRect()
    const wrapRect = this.layerWrap.getBoundingClientRect()

    this.layerWrap.style.setProperty('--indicator-width', `${buttonRect.width}px`)
    this.layerWrap.style.setProperty('--indicator-offset', `${buttonRect.left - wrapRect.left}px`)
  }
}

const navMainDesktop = new NavMainDesktop()
navMainDesktop.init()
